/*global WebView*/
/**
 * Contextual help : highlight of the screen zones described by a help row (roadmap lot L1.7c,
 * spec docs/superpowers/specs/2026-10-09-mise-en-evidence-spike.md § 3).
 *
 * The build (tools/doc-build/lib/help-render.js) puts data-help-highlight="<css selector>" on
 * the table rows / list items of the documentation that describe a zone of the screen, and
 * index.json carries `views` : screen code → view files (= data-viewname of the rendered view).
 * Hovering or focusing such a row outlines the zone(s) in the current screen :
 *   - scope : the view of the topmost screen of the route stack that has a known view (a popup
 *     included), never the whole document (the dashboard, the list and the form can be in the
 *     DOM at the same time, with the same fields) ;
 *   - only the visible elements count ; a row whose zone is absent (right, display condition,
 *     inactive tab) gets a grey dot and a tooltip, never an error ;
 *   - fixed outline boxes (z-index 1064 : under the panel, above the Bootstrap modals),
 *     scrolled into view, redrawn on scroll / resize / re-rendering of the view ;
 *   - cleared on screen change, on new panel content and when the panel closes.
 * Disabled when the panel takes the whole width (small screens) : nothing to see beside it.
 *
 * The pure functions (exported for jest) decide ; the singleton only touches the DOM.
 */

const HOVER_DELAY = 120 ;
const BOX_MARGIN = 4 ;
const ROW = "[data-help-highlight]" ;
//under this viewport width the panel covers the whole screen (helpPanel.scss)
const FULL_WIDTH_MAX = 767.98 ;

//──────────────────────────── pure functions ────────────────────────────

/**
 * Split a selector list on its top-level commas (not inside (), [] or quotes) : each part is
 * queried on its own, so that one selector the browser rejects does not hide the others
 *
 * @param {string} selector "a, b:has(> c, d), [x=\"1,2\"]"
 * @return {string[]} trimmed, non empty parts
 */
function splitSelector(selector){
    const parts = [] ;
    let depth = 0 ;
    let quote = null ;
    let current = "" ;
    const text = String(selector || "") ;
    for(let i=0; i<text.length; i++){
        const c = text[i] ;
        if(quote){
            if(c === "\\" && i+1 < text.length){ current += c+text[++i] ; continue ; }
            if(c === quote){ quote = null ; }
        }else if(c === "\"" || c === "'"){
            quote = c ;
        }else if(c === "(" || c === "["){
            depth++ ;
        }else if((c === ")" || c === "]") && depth > 0){
            depth-- ;
        }else if(c === "," && depth === 0){
            if(current.trim()){ parts.push(current.trim()) ; }
            current = "" ;
            continue ;
        }
        current += c ;
    }
    if(current.trim()){ parts.push(current.trim()) ; }
    return parts ;
}

/**
 * Does a rendered view (its data-viewname) match a view file of the manifest ?
 *
 * @param {string} viewName data-viewname, ex: "modules/purchase/views/purchase/purchasePartialPopup"
 * @param {string} file manifest view file (same form ; a bare view name is accepted too)
 * @return {boolean}
 */
function viewMatches(viewName, file){
    if(!viewName || !file){ return false ; }
    const a = String(viewName).replace(/^\.?\//, "") ;
    const b = String(file).replace(/^\.?\//, "").replace(/^client\/src\//, "") ;
    return a === b || a.slice(-(b.length+1)) === "/"+b ;
}

/**
 * Screen whose view bounds the search : the topmost route of the stack with views in the
 * manifest (a popup over a form, a form over a list)
 *
 * @param {string[]} routeNames AppController.getActiveRouteNames() : bottom to top, with or without "#"
 * @param {object} views index.json `views` : {"#code": [view files]}
 * @return {object|null} {code, files} or null if no displayed screen has a known view
 */
function scopeScreen(routeNames, views){
    const known = views || {} ;
    const names = routeNames || [] ;
    for(let i=names.length-1; i>=0; i--){
        const name = String(names[i] || "") ;
        if(!name){ continue ; }
        const code = name.charAt(0) === "#" ? name : "#"+name ;
        if(known[code] && known[code].length){ return {code: code, files: known[code]} ; }
    }
    return null ;
}

/**
 * Is an element displayed ? (simulated in tests with plain objects)
 *
 * @param {number} rectCount el.getClientRects().length (0 when display:none, detached, or
 *  inside a display:none ancestor)
 * @param {object} rect el.getBoundingClientRect() {width, height}
 * @param {string} visibility computed style visibility
 * @return {boolean}
 */
function isDisplayed(rectCount, rect, visibility){
    if(!rectCount || !rect){ return false ; }
    if(visibility === "hidden" || visibility === "collapse"){ return false ; }
    return rect.width > 0 || rect.height > 0 ;
}

/**
 * Outline box of a zone, in viewport coordinates
 *
 * @param {object} rect {left, top, width, height} of the zone
 * @param {number} [margin] space around the zone
 * @return {object} {left, top, width, height}
 */
function boxFor(rect, margin){
    const m = margin === undefined ? BOX_MARGIN : margin ;
    return {left: rect.left - m, top: rect.top - m, width: rect.width + 2*m, height: rect.height + 2*m} ;
}

/**
 * Does the zone need scrolling to be seen ? Out of the viewport, or under the panel
 *
 * @param {object} rect zone rectangle (viewport coordinates)
 * @param {object} viewport {width, height, top (top bar bottom), right (left edge of the panel, or width)}
 * @return {boolean}
 */
function needsScroll(rect, viewport){
    const top = viewport.top || 0 ;
    const right = viewport.right === undefined ? viewport.width : viewport.right ;
    return rect.top < top || rect.bottom > viewport.height || rect.left < 0 || rect.right > right ;
}

/**
 * Part of a zone actually seen : its rectangle cut by the scrolling containers around it (a
 * grid column scrolled out of its grid) and by the visible part of the screen
 *
 * @param {object} rect {left, top, right, bottom} of the zone
 * @param {object[]} clips rectangles of the ancestors that clip their content, and the viewport
 * @return {object|null} {left, top, right, bottom, width, height}, null if nothing is seen
 */
function clipRect(rect, clips){
    let left = rect.left ;
    let top = rect.top ;
    let right = rect.right ;
    let bottom = rect.bottom ;
    (clips || []).forEach((c) => {
        left = Math.max(left, c.left) ;
        top = Math.max(top, c.top) ;
        right = Math.min(right, c.right) ;
        bottom = Math.min(bottom, c.bottom) ;
    }) ;
    if(right - left < 1 || bottom - top < 1){ return null ; }
    return {left: left, top: top, right: right, bottom: bottom, width: right - left, height: bottom - top} ;
}

//──────────────────────────── DOM singleton ────────────────────────────

const helpHighlight = {
    panel: null,
    views: null,
    boxes: [],
    active: null, //the row whose zones are shown
    tour: null, //helpTour while a guided tour runs : hover and focus leave the outline alone
    onAvailability: null, //called with `enabled` once the rows are marked (helpTour buttons)
    hoverTimer: null,
    frame: null,
    observer: null,
} ;

function tr(key){
    try{
        return WebView.tr(key) ;
    }catch(err){
        return key ;
    }
}

function reducedMotion(){
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) ;
}

function displayed(el){
    const style = window.getComputedStyle(el) ;
    return isDisplayed(el.getClientRects().length, el.getBoundingClientRect(), style && style.visibility) ;
}

/**
 * Wire the highlight to the panel. Idempotent.
 *
 * @param {object} panel helpPanel (host, appController, opened)
 */
helpHighlight.attach = function(panel){
    if(this.panel){ return ; }
    this.panel = panel ;
    const host = panel.host ;
    host.addEventListener("mouseover", (ev) => this.onOver(ev)) ;
    host.addEventListener("mouseout", (ev) => this.onOut(ev)) ;
    host.addEventListener("focusin", (ev) => this.onFocus(ev)) ;
    host.addEventListener("focusout", (ev) => this.onBlur(ev)) ;
    //the view may have changed since the rows were marked (record loaded, block shown) : the
    //availability is read again when the pointer comes back to the panel
    host.addEventListener("mouseenter", () => this.refreshAvailability()) ;
    const redraw = () => this.schedule() ;
    window.addEventListener("scroll", redraw, true) ;//capture : any scrolling container
    window.addEventListener("resize", redraw) ;
    if(panel.appController && panel.appController.on){
        panel.appController.on("routeChanged", () => {
            this.clear() ;
            setTimeout(() => this.refreshAvailability(), 400) ;//once the new view is rendered
        }) ;
    }
} ;

/**
 * Highlight off : the panel is closed or covers the whole screen
 */
helpHighlight.enabled = function(){
    return !!(this.panel && this.panel.opened && window.innerWidth > FULL_WIDTH_MAX) ;
} ;

/**
 * New panel content : forget the outline, mark the rows (focusable, zone present or not)
 *
 * @param {object} index the manifest displayed (its `views`)
 */
helpHighlight.render = function(index){
    this.clear() ;
    this.views = (index && index.views) || null ;
    if(!this.panel){ return ; }
    const rows = this.panel.host.querySelectorAll(ROW) ;
    for(let i=0; i<rows.length; i++){
        //keyboard users reach the zone too ; nothing more is announced than the row text
        rows[i].setAttribute("tabindex", "0") ;
    }
    this.refreshAvailability() ;
} ;

/**
 * Root element of the current screen view, or null (no known view displayed)
 */
helpHighlight.scope = function(){
    const app = this.panel && this.panel.appController ;
    const screen = scopeScreen(app && app.getActiveRouteNames ? app.getActiveRouteNames() : [], this.views) ;
    if(!screen){ return null ; }
    const candidates = document.querySelectorAll("[data-viewname]") ;
    let found = null ;
    for(let i=0; i<candidates.length; i++){
        const name = candidates[i].getAttribute("data-viewname") ;
        if(screen.files.some((f) => viewMatches(name, f)) && displayed(candidates[i])){
            found = candidates[i] ;//the last one in document order : the most recently opened
        }
    }
    return found ;
} ;

/**
 * Visible elements of the current screen matching a row selector
 *
 * @param {string} selector data-help-highlight value
 * @param {Element|null} [scopeRoot] the current screen view, if already looked up
 * @return {Element[]}
 */
helpHighlight.targets = function(selector, scopeRoot){
    const root = scopeRoot === undefined ? this.scope() : scopeRoot ;
    if(!root){ return [] ; }
    const found = [] ;
    splitSelector(selector).forEach((part) => {
        let els = [] ;
        try{
            els = root.querySelectorAll(part) ;
        }catch(err){
            //rejected by this browser (`:has` before Chrome 105 / Firefox 121) : zone absent
            return ;
        }
        for(let i=0; i<els.length; i++){
            if(found.indexOf(els[i]) === -1 && displayed(els[i])){ found.push(els[i]) ; }
        }
    }) ;
    return found ;
} ;

/**
 * Grey dot + tooltip on the rows whose zone is not on the screen
 */
helpHighlight.refreshAvailability = function(){
    if(!this.panel || !this.panel.host){ return ; }
    const host = this.panel.host ;
    const on = this.enabled() ;
    host.classList.toggle("help-hl-off", !on) ;
    const rows = on ? host.querySelectorAll(ROW) : [] ;
    const missingText = tr("help.zoneMissing") ;
    const root = this.scope() ;
    for(let i=0; i<rows.length; i++){
        const missing = this.targets(rows[i].getAttribute("data-help-highlight"), root).length === 0 ;
        rows[i].classList.toggle("help-hl-missing", missing) ;
        if(missing){ rows[i].setAttribute("title", missingText) ; }
        else{ rows[i].removeAttribute("title") ; }
    }
    if(this.onAvailability){ this.onAvailability(on) ; }
} ;

helpHighlight.rowOf = function(target){
    const row = target && target.closest ? target.closest(ROW) : null ;
    return row && this.panel.host.contains(row) ? row : null ;
} ;

helpHighlight.onOver = function(ev){
    if(this.tour){ return ; }
    const row = this.rowOf(ev.target) ;
    if(!row || row === this.active){ return ; }
    clearTimeout(this.hoverTimer) ;
    this.hoverTimer = setTimeout(() => this.show(row), HOVER_DELAY) ;
} ;

helpHighlight.onOut = function(ev){
    if(this.tour){ return ; }
    const row = this.rowOf(ev.target) ;
    if(!row || (ev.relatedTarget && row.contains(ev.relatedTarget))){ return ; }
    clearTimeout(this.hoverTimer) ;
    if(row === this.active && document.activeElement !== row){ this.clear() ; }
} ;

helpHighlight.onFocus = function(ev){
    if(this.tour){ return ; }
    const row = this.rowOf(ev.target) ;
    if(row && ev.target === row){ this.show(row) ; }
} ;

helpHighlight.onBlur = function(ev){
    if(this.tour){ return ; }
    const row = this.rowOf(ev.target) ;
    if(row && row === this.active && !row.matches(":hover")){ this.clear() ; }
} ;

/**
 * Outline the zones of a row, scrolling the screen to the first one if needed
 *
 * @param {Element} row a [data-help-highlight] row of the panel
 * @return {Element[]} the zones outlined
 */
helpHighlight.show = function(row){
    this.clear() ;
    if(!this.enabled()){ return [] ; }
    const els = this.targets(row.getAttribute("data-help-highlight")) ;
    row.classList.toggle("help-hl-missing", els.length === 0) ;
    if(!els.length){ return [] ; }
    this.active = row ;
    row.classList.add("help-hl-active") ;
    this.reveal(els) ;
    this.draw(els) ;
    this.observe() ;
    return els ;
} ;

/**
 * Scroll the screen (and the grids) so that the zones are seen : the first one centered when
 * it is out of the screen, the others brought in with the least move
 *
 * @param {Element[]} els the zones, in document order
 */
helpHighlight.reveal = function(els){
    const viewport = this.viewport() ;
    const hidden = els.filter((el) => {
        const rect = el.getBoundingClientRect() ;
        const seen = this.seenRect(el, viewport) ;
        return !seen || seen.width < rect.width - 1 || seen.height < rect.height - 1 ;
    }) ;
    if(!hidden.length){ return ; }
    //several moves in a row : no smooth scrolling, it would stop at the first one
    const behavior = hidden.length === 1 && !reducedMotion() ? "smooth" : "auto" ;
    hidden.slice(1).reverse().forEach((el) => el.scrollIntoView({block: "nearest", inline: "nearest", behavior: "auto"})) ;
    const first = hidden[0] ;
    const far = needsScroll(first.getBoundingClientRect(), viewport) && first === els[0] ;
    first.scrollIntoView({block: far ? "center" : "nearest", inline: "nearest", behavior: behavior}) ;
} ;

/**
 * Seen part of a zone (clipping ancestors, visible part of the screen), null if hidden
 */
helpHighlight.seenRect = function(el, viewport){
    const clips = [{left: 0, top: viewport.top, right: viewport.right, bottom: viewport.height}] ;
    for(let p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement){
        const style = window.getComputedStyle(p) ;
        if(style.overflowX !== "visible" || style.overflowY !== "visible"){
            clips.push(p.getBoundingClientRect()) ;
        }
        if(style.position === "fixed"){ break ; }//nothing above clips a fixed container
    }
    return clipRect(el.getBoundingClientRect(), clips) ;
} ;

/**
 * Visible part of the screen : under the top bar, left of the panel when it overlays the screen
 */
helpHighlight.viewport = function(){
    const host = this.panel.host ;
    const panelLeft = host.getBoundingClientRect().left ;
    return {
        width: window.innerWidth,
        height: window.innerHeight,
        top: parseInt(host.style.top, 10) || 0,
        right: panelLeft > 0 ? panelLeft : window.innerWidth,
    } ;
} ;

helpHighlight.draw = function(els){
    this.removeBoxes() ;
    const still = reducedMotion() ;
    const viewport = this.viewport() ;
    els.forEach((el) => {
        //only the seen part : a zone scrolled out of its grid is not outlined over the panel
        const seen = this.seenRect(el, viewport) ;
        if(!seen){ return ; }
        const b = boxFor(seen) ;
        const box = document.createElement("div") ;
        box.className = "help-hl-box"+(still ? " help-hl-still" : "") ;
        box.setAttribute("aria-hidden", "true") ;
        box.setAttribute("data-help-hl-box", "") ;
        box.style.left = b.left+"px" ;
        box.style.top = b.top+"px" ;
        box.style.width = b.width+"px" ;
        box.style.height = b.height+"px" ;
        document.body.appendChild(box) ;
        this.boxes.push(box) ;
    }) ;
    if(this.tour){ this.tour.redraw() ; }//spotlight and bubble follow the outline
} ;

/**
 * Redraw at the next frame (scroll, resize, view re-rendered) ; the zone may have disappeared
 */
helpHighlight.schedule = function(){
    if(!this.active || this.frame){ return ; }
    this.frame = window.requestAnimationFrame(() => {
        this.frame = null ;
        if(!this.active){ return ; }
        if(!this.enabled() || !document.body.contains(this.active)){
            return this.tour ? this.tour.end() : this.clear() ;
        }
        const els = this.targets(this.active.getAttribute("data-help-highlight")) ;
        if(!els.length){ return this.tour ? this.tour.lost() : this.clear() ; }
        this.draw(els) ;
    }) ;
} ;

/**
 * Follow the re-rendering of the view while a zone is shown (and only then)
 */
helpHighlight.observe = function(){
    const root = this.scope() ;
    if(!root || !window.MutationObserver){ return ; }
    this.observer = new MutationObserver(() => this.schedule()) ;
    this.observer.observe(root, {childList: true, subtree: true, attributes: true, attributeFilter: ["class", "style", "hidden"]}) ;
} ;

helpHighlight.removeBoxes = function(){
    this.boxes.forEach((b) => { if(b.parentNode){ b.parentNode.removeChild(b) ; } }) ;
    this.boxes = [] ;
} ;

/**
 * No outline left : screen change, new content, panel closed
 */
helpHighlight.clear = function(){
    clearTimeout(this.hoverTimer) ;
    if(this.frame){
        window.cancelAnimationFrame(this.frame) ;
        this.frame = null ;
    }
    if(this.observer){
        this.observer.disconnect() ;
        this.observer = null ;
    }
    if(this.active){ this.active.classList.remove("help-hl-active") ; }
    this.active = null ;
    this.removeBoxes() ;
} ;

helpHighlight.splitSelector = splitSelector ;
helpHighlight.viewMatches = viewMatches ;
helpHighlight.scopeScreen = scopeScreen ;
helpHighlight.isDisplayed = isDisplayed ;
helpHighlight.boxFor = boxFor ;
helpHighlight.clipRect = clipRect ;
helpHighlight.needsScroll = needsScroll ;

module.exports = helpHighlight ;
